import { DashboardLayout } from "@/projects/components/dashboard-layout";
import {
  createTopic,
  deleteTopic,
  fetchTopicsPage,
  getTopicApiErrorMessage,
  isoToLocalInput,
  localInputToIso,
  setTopicActive,
  updateTopic,
  type Topic,
  type TopicWritePayload,
} from "@/projects/lib/topicsApi";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/projects/components/ui/table";
import { Button } from "@/projects/components/ui/button";
import { Input } from "@/projects/components/ui/input";
import { Label } from "@/projects/components/ui/label";
import { Switch } from "@/projects/components/ui/switch";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/projects/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/projects/components/ui/alert-dialog";
import { useEffect, useState } from "react";
import { Plus, Edit, Power, Trash2, Loader2 } from "lucide-react";
import { toast } from "@/projects/lib/toast";
import { cn } from "@/projects/lib/utils";
import {
  DataPagination,
  useServerPagination,
} from "@/projects/components/data-pagination";

const EMPTY_FORM = {
  name: "",
  minAge: "",
  maxAge: "",
  size: "",
  startTime: "",
  endTime: "",
  isActive: true,
};

function formatDateTime(iso: string | null): string {
  if (!iso) {
    return "—";
  }
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? "—" : d.toLocaleString();
}

export function TopicsPage() {
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState<Topic | null>(null);
  const [confirmToggle, setConfirmToggle] = useState<Topic | null>(null);
  const [confirmDelete, setConfirmDelete] = useState<Topic | null>(null);
  const [toggling, setToggling] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const pagination = useServerPagination(
    ({ page, pageSize }) => fetchTopicsPage({ page, pageSize }),
    [],
    {
      onError: error =>
        toast.error(getTopicApiErrorMessage(error, "Failed to load topics.")),
    }
  );
  const topics = pagination.items;
  const loading = pagination.loading;

  return (
    <DashboardLayout
      role="admin"
      title="Topics"
      breadcrumb={["Home", "Topics"]}
    >
      <div className="mb-4 flex items-center justify-between">
        <div className="text-muted-foreground text-sm">
          {loading ? "Loading…" : `${pagination.meta.count} topics`}
        </div>
        <Button
          onClick={() => {
            setEditing(null);
            setOpen(true);
          }}
        >
          <Plus className="mr-2 h-4 w-4" /> Add Topic
        </Button>
      </div>

      <div className="bg-card shadow-soft overflow-hidden rounded-xl border">
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow className="bg-muted/50">
                <TableHead>Name</TableHead>
                <TableHead className="text-center">Min Age</TableHead>
                <TableHead className="text-center">Max Age</TableHead>
                <TableHead className="text-center">Video Size (MB)</TableHead>
                <TableHead>Start Time</TableHead>
                <TableHead>End Time</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading && (
                <TableRow>
                  <TableCell colSpan={8} className="py-16 text-center">
                    <div className="text-muted-foreground inline-flex items-center gap-2 text-sm">
                      <Loader2 className="h-4 w-4 animate-spin" />
                      Loading topics…
                    </div>
                  </TableCell>
                </TableRow>
              )}
              {!loading && topics.length === 0 && (
                <TableRow>
                  <TableCell
                    colSpan={8}
                    className="text-muted-foreground py-16 text-center text-sm"
                  >
                    No topics found. Add your first topic to get started.
                  </TableCell>
                </TableRow>
              )}
              {!loading &&
                topics.map(t => (
                  <TableRow key={t.id}>
                    <TableCell>
                      <div className="font-medium">{t.name}</div>
                    </TableCell>
                    <TableCell className="text-center">
                      {t.minAge ?? "—"}
                    </TableCell>
                    <TableCell className="text-center">
                      {t.maxAge ?? "—"}
                    </TableCell>
                    <TableCell className="text-center">
                      {t.size != null ? `${t.size} MB` : "—"}
                    </TableCell>
                    <TableCell className="text-muted-foreground text-xs">
                      {formatDateTime(t.startTime)}
                    </TableCell>
                    <TableCell className="text-muted-foreground text-xs">
                      {formatDateTime(t.endTime)}
                    </TableCell>
                    <TableCell>
                      <span
                        className={cn(
                          "inline-flex items-center gap-1.5 rounded-full border px-2 py-0.5 text-xs font-medium",
                          t.isActive
                            ? "border-success/30 bg-success/10 text-success"
                            : "border-muted-foreground/30 bg-muted text-muted-foreground"
                        )}
                      >
                        <span
                          className={cn(
                            "h-1.5 w-1.5 rounded-full",
                            t.isActive ? "bg-success" : "bg-muted-foreground"
                          )}
                        />
                        {t.isActive ? "Active" : "Inactive"}
                      </span>
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-1">
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => {
                            setEditing(t);
                            setOpen(true);
                          }}
                        >
                          <Edit className="h-3.5 w-3.5" />
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => setConfirmToggle(t)}
                        >
                          <Power className="h-3.5 w-3.5" />
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          className="text-destructive hover:text-destructive"
                          onClick={() => setConfirmDelete(t)}
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
            </TableBody>
          </Table>
        </div>
        {!loading && topics.length > 0 && (
          <DataPagination pagination={pagination} itemLabel="topics" />
        )}
      </div>

      <TopicFormDialog
        open={open}
        onOpenChange={setOpen}
        topic={editing}
        onSaved={() => pagination.reload()}
      />

      <AlertDialog
        open={!!confirmToggle}
        onOpenChange={o => !o && setConfirmToggle(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {confirmToggle?.isActive ? "Deactivate" : "Activate"} topic?
            </AlertDialogTitle>
            <AlertDialogDescription>
              {confirmToggle?.isActive
                ? `"${confirmToggle?.name}" will be marked inactive.`
                : `"${confirmToggle?.name}" will be marked active.`}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={toggling}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              disabled={toggling}
              onClick={async e => {
                e.preventDefault();
                if (!confirmToggle) {
                  return;
                }
                setToggling(true);
                try {
                  await setTopicActive(
                    confirmToggle.id,
                    !confirmToggle.isActive
                  );
                  toast.success(
                    `Topic ${confirmToggle.isActive ? "deactivated" : "activated"}`
                  );
                  setConfirmToggle(null);
                  pagination.reload();
                } catch (error) {
                  toast.error(
                    getTopicApiErrorMessage(
                      error,
                      "Failed to update topic status."
                    )
                  );
                } finally {
                  setToggling(false);
                }
              }}
            >
              {toggling ? "Saving…" : "Confirm"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog
        open={!!confirmDelete}
        onOpenChange={o => !o && setConfirmDelete(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete topic?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete "{confirmDelete?.name}". This action
              cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleting}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              disabled={deleting}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={async e => {
                e.preventDefault();
                if (!confirmDelete) {
                  return;
                }
                setDeleting(true);
                try {
                  await deleteTopic(confirmDelete.id);
                  toast.success("Topic deleted");
                  setConfirmDelete(null);
                  pagination.reload();
                } catch (error) {
                  toast.error(
                    getTopicApiErrorMessage(error, "Failed to delete topic.")
                  );
                } finally {
                  setDeleting(false);
                }
              }}
            >
              {deleting ? "Deleting…" : "Delete"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </DashboardLayout>
  );
}

function TopicFormDialog({
  open,
  onOpenChange,
  topic,
  onSaved,
}: {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  topic: Topic | null;
  onSaved: () => void;
}) {
  const [f, setF] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) {
      return;
    }
    setF(
      topic
        ? {
            name: topic.name,
            minAge: topic.minAge !== null ? String(topic.minAge) : "",
            maxAge: topic.maxAge !== null ? String(topic.maxAge) : "",
            size: topic.size !== null ? String(topic.size) : "",
            startTime: isoToLocalInput(topic.startTime),
            endTime: isoToLocalInput(topic.endTime),
            isActive: topic.isActive,
          }
        : EMPTY_FORM
    );
  }, [open, topic]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const name = f.name.trim();
    if (!name) {
      toast.error("Topic name is required.");
      return;
    }

    let minAge: number | undefined;
    if (f.minAge.trim()) {
      const n = Number(f.minAge);
      if (Number.isNaN(n) || n < 0) {
        toast.error("Please enter a valid minimum age.");
        return;
      }
      minAge = n;
    }

    let maxAge: number | undefined;
    if (f.maxAge.trim()) {
      const n = Number(f.maxAge);
      if (Number.isNaN(n) || n < 0) {
        toast.error("Please enter a valid maximum age.");
        return;
      }
      maxAge = n;
    }

    if (minAge !== undefined && maxAge !== undefined && maxAge < minAge) {
      toast.error("Maximum age must be greater than or equal to minimum age.");
      return;
    }

    let size: number | undefined;
    if (f.size.trim()) {
      const n = Number(f.size);
      if (Number.isNaN(n) || n <= 0) {
        toast.error("Please enter a valid video size in MB.");
        return;
      }
      size = n;
    }

    if (!f.startTime || !f.endTime) {
      toast.error("Start time and end time are required.");
      return;
    }
    const startIso = localInputToIso(f.startTime);
    const endIso = localInputToIso(f.endTime);
    if (new Date(endIso).getTime() <= new Date(startIso).getTime()) {
      toast.error("End time must be after start time.");
      return;
    }

    setSaving(true);
    try {
      const payload: TopicWritePayload = {
        name,
        start_time: startIso,
        end_time: endIso,
        is_active: f.isActive,
      };
      if (minAge !== undefined) {
        payload.min_age = minAge;
      }
      if (maxAge !== undefined) {
        payload.max_age = maxAge;
      }
      if (size !== undefined) {
        payload.size = size;
      }
      if (topic) {
        await updateTopic(topic.id, payload);
        toast.success("Topic updated");
      } else {
        await createTopic(payload);
        toast.success("Topic added successfully");
      }
      onOpenChange(false);
      onSaved();
    } catch (error) {
      toast.error(
        getTopicApiErrorMessage(
          error,
          topic ? "Failed to update topic." : "Failed to add topic."
        )
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>{topic ? "Edit Topic" : "Add Topic"}</DialogTitle>
        </DialogHeader>
        <form
          onSubmit={e => void handleSubmit(e)}
          className="grid gap-3 sm:grid-cols-2"
        >
          <Field label="Topic Name *" className="sm:col-span-2">
            <Input
              value={f.name}
              onChange={e => setF({ ...f, name: e.target.value })}
              placeholder="e.g. Bhaktamar Recitation"
              disabled={saving}
            />
          </Field>
          <Field label="Min Age">
            <Input
              type="number"
              min={0}
              value={f.minAge}
              onChange={e => setF({ ...f, minAge: e.target.value })}
              placeholder="e.g. 8"
              disabled={saving}
            />
          </Field>
          <Field label="Max Age">
            <Input
              type="number"
              min={0}
              value={f.maxAge}
              onChange={e => setF({ ...f, maxAge: e.target.value })}
              placeholder="e.g. 12"
              disabled={saving}
            />
          </Field>
          <Field label="Video Size (MB)" className="sm:col-span-2">
            <Input
              type="number"
              min={1}
              step={1}
              value={f.size}
              onChange={e => setF({ ...f, size: e.target.value })}
              placeholder="e.g. 100"
              disabled={saving}
            />
            <p className="text-muted-foreground mt-1 text-[11px]">
              Maximum video upload size allowed for this topic (MB).
            </p>
          </Field>

          <Field label="Start Time *">
            <Input
              type="datetime-local"
              value={f.startTime}
              onChange={e => setF({ ...f, startTime: e.target.value })}
              disabled={saving}
            />
          </Field>
          <Field label="End Time *">
            <Input
              type="datetime-local"
              value={f.endTime}
              onChange={e => setF({ ...f, endTime: e.target.value })}
              disabled={saving}
            />
          </Field>
          <div className="mt-2 flex items-center gap-2 sm:col-span-2">
            <Switch
              checked={f.isActive}
              onCheckedChange={v => setF({ ...f, isActive: v })}
              disabled={saving}
            />
            <Label className="text-sm">
              {f.isActive ? "Active" : "Inactive"}
            </Label>
          </div>
          <DialogFooter className="sm:col-span-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={saving}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={saving}>
              {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {topic ? "Save Changes" : "Add Topic"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function Field({
  label,
  children,
  className,
}: {
  label: string;
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <div className={className}>
      <Label className="text-xs">{label}</Label>
      {children}
    </div>
  );
}
